'use client'

import { useEffect } from 'react'
import { trackLandingPageLoaded, type LandingPageId } from '@/lib/analytics'
import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'

interface LandingPageLatencyTrackerProps {
    pageId: LandingPageId
    skuId?: string | null
}

/** Fires `landing_page_loaded` once after the browser finishes loading.
 *  Idempotency lives in `trackLandingPageLoaded` (module-level Set keyed
 *  by page_view_id), so this component just owns the listener wiring. */
export default function LandingPageLatencyTracker({ pageId, skuId }: LandingPageLatencyTrackerProps) {
    useEffect(() => {
        const fire = () =>
            trackLandingPageLoaded({
                pageId,
                skuId: skuId ?? null,
                isAuthed: isAuthenticatedViaMiddlewareCookie(),
            })

        // Already loaded → fire next tick so the latest LCP entry is observable.
        if (document.readyState === 'complete') {
            const t = setTimeout(fire, 0)
            return () => clearTimeout(t)
        }

        window.addEventListener('load', fire, { once: true })
        // bfcache restore: `pageshow.persisted=true` skips the `load` event.
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted) fire()
        }
        window.addEventListener('pageshow', onPageShow)

        return () => {
            window.removeEventListener('load', fire)
            window.removeEventListener('pageshow', onPageShow)
        }
    }, [pageId, skuId])

    return null
}
